import { ModelSection } from '../components/models/types';
import { ApiModel } from '../components/models/apiTypes';

/**
 * Resolves a model identifier (ID, modelIdentifier, or name) to a human-readable model name
 * by searching through the model registry sections.
 * 
 * @param modelIdentifier - The model identifier to resolve (could be ID, modelIdentifier, or name)
 * @param sections - The model sections from useImageModels hook
 * @returns The human-readable model name, or null if not found
 */
export const resolveModelName = (
  modelIdentifier: string | null | undefined,
  sections: ModelSection[]
): string | null => {
  if (!modelIdentifier || !sections || sections.length === 0) {
    return null;
  }

  // Search through all sections and models
  for (const section of sections) {
    for (const model of section.models) {
      const apiModel = model.apiModel;
      if (!apiModel) continue;

      // Check if the identifier matches:
      // 1. Model ID (database ID)
      // 2. Model identifier (e.g., "fal-ai/flux-lora")
      // 3. Model name (human-readable name)
      if (
        apiModel.id === modelIdentifier ||
        apiModel.modelIdentifier === modelIdentifier ||
        apiModel.name === modelIdentifier ||
        model.title === modelIdentifier
      ) {
        // Return the human-readable name (title from ModelCard)
        return model.title;
      }
    }
  }

  return null;
};

/**
 * Resolves a model identifier to the full ApiModel object
 * 
 * @param modelIdentifier - The model identifier to resolve
 * @param sections - The model sections from useImageModels hook
 * @returns The ApiModel object, or null if not found
 */
export const resolveModelData = (
  modelIdentifier: string | null | undefined,
  sections: ModelSection[]
): ApiModel | null => {
  if (!modelIdentifier || !sections || sections.length === 0) {
    return null;
  }

  for (const section of sections) {
    for (const model of section.models) {
      const apiModel = model.apiModel;
      if (!apiModel) continue;

      if (
        apiModel.id === modelIdentifier ||
        apiModel.modelIdentifier === modelIdentifier ||
        apiModel.name === modelIdentifier ||
        model.title === modelIdentifier
      ) {
        return apiModel;
      }
    }
  }

  return null;
};

